import { ApolloClient, ApolloLink, HttpLink } from "@apollo/client";
import { notifyError } from "common/utils/app-monitoring/notify";
import { RetryLink } from "@apollo/client/link/retry";
import { createCache } from "./createCache";
import { csrfHeaderLink, csrfRetryLink } from "./csrfLink";

// 4xx errors (403, 401, 422, etc.) indicate expected auth/session failures and
// should not be reported as application errors. Only 5xx or network-level errors
// (no statusCode) indicate actual bugs worth reporting. 2xx/3xx status codes on
// parse errors (e.g. CDN returning HTML with 200) should still be reported.
const isServerOrNetworkError = (error: any): boolean => {
  const statusCode = error?.statusCode;
  if (typeof statusCode === "number") {
    return statusCode >= 500 || statusCode < 400;
  }
  // No statusCode means a network-level failure (DNS, timeout, etc.)
  return true;
};

export const graphqlClient = new ApolloClient({
  cache: createCache(),
  queryDeduplication: true,
  defaultOptions: {
    watchQuery: {
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "cache-first"
    },
    query: {
      errorPolicy: "all",
      fetchPolicy: "cache-first"
    },
    mutate: {
      errorPolicy: "all"
    }
  },
  link: ApolloLink.from([
    csrfRetryLink,
    new RetryLink({
      attempts: {
        max: 3,
        // RetryLink passes the raw network error to retryIf, so
        // error.statusCode is the correct path here (not
        // error.networkError?.statusCode, which applies to ApolloError).
        retryIf: (error, operation) => {
          if (isServerOrNetworkError(error) && typeof window !== "undefined") {
            notifyError(`[GraphQL] ${error.message}`, {
              context: { operationName: operation.operationName }
            });
          }

          // Type guard to check if error has Apollo-specific properties
          const hasNetworkError = "networkError" in error && error.networkError;
          const hasGraphQLErrors = "graphQLErrors" in error && Array.isArray(error.graphQLErrors);

          // Only retry network errors or server-side 5xx responses
          return !!(
            hasNetworkError ||
            (hasGraphQLErrors &&
              (error as any).graphQLErrors.some((e: any) => e.extensions?.code >= 500))
          );
        }
      }
    }),
    csrfHeaderLink,
    new HttpLink({
      uri: "/graphql",
      credentials: "include"
    })
  ])
});
